import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronRight, LogOut } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import LanguageToggle from '../LanguageToggle';
import BottomSheet from '../student/BottomSheet';
import { TemplatePuzzleGlyph } from '../ui/ActionGlyphs';
import ClipboardListIcon from '../ui/ClipboardIcon';

const ReceiptIcon = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" fill="currentColor" {...props}>
    <path d="M328 112l-144 0-37.3-74.5c-1.8-3.6-2.7-7.6-2.7-11.6 0-14.3 11.6-25.9 25.9-25.9L342.1 0c14.3 0 25.9 11.6 25.9 25.9 0 4-.9 8-2.7 11.6L328 112zM169.6 160l172.8 0 48.7 40.6C457.6 256 496 338 496 424.5 496 472.8 456.8 512 408.5 512l-305.1 0C55.2 512 16 472.8 16 424.5 16 338 54.4 256 120.9 200.6L169.6 160zM260 224c-11 0-20 9-20 20l0 4c-28.8 .3-52 23.7-52 52.5 0 25.7 18.5 47.6 43.9 51.8l41.7 7c6 1 10.4 6.2 10.4 12.3 0 6.9-5.6 12.5-12.5 12.5L216 384c-11 0-20 9-20 20s9 20 20 20l24 0 0 4c0 11 9 20 20 20s20-9 20-20l0-4.7c25-4.1 44-25.7 44-51.8 0-25.7-18.5-47.6-43.9-51.8l-41.7-7c-6-1-10.4-6.2-10.4-12.3 0-6.9 5.6-12.5 12.5-12.5l47.5 0c11 0 20-9 20-20s-9-20-20-20l-8 0 0-4c0-11-9-20-20-20z" />
  </svg>
);

// Font Awesome solid gift / triangle-exclamation.
const GiftIcon = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" fill="currentColor" {...props}>
    <path d="M190.5 68.8L225.3 128l-1.3 0-72 0c-22.1 0-40-17.9-40-40s17.9-40 40-40l2.2 0c14.9 0 28.8 7.9 36.3 20.8zM64 88c0 14.4 3.5 28 9.6 40L32 128c-17.7 0-32 14.3-32 32l0 64c0 17.7 14.3 32 32 32l448 0c17.7 0 32-14.3 32-32l0-64c0-17.7-14.3-32-32-32l-41.6 0c6.1-12 9.6-25.6 9.6-40c0-48.6-39.4-88-88-88l-2.2 0c-31.9 0-61.5 16.9-77.7 44.4L256 85.5l-24.1-41C215.7 16.9 186.1 0 154.2 0L152 0C103.4 0 64 39.4 64 88zm336 0c0 22.1-17.9 40-40 40l-72 0-1.3 0 34.8-59.2C329.1 55.9 342.9 48 357.8 48l2.2 0c22.1 0 40 17.9 40 40zM32 288l0 176c0 26.5 21.5 48 48 48l144 0 0-224L32 288zM288 512l144 0c26.5 0 48-21.5 48-48l0-176-192 0 0 224z" />
  </svg>
);

const SheetRow = ({ icon, label, onClick, dot = false }) => {
  const Icon = icon;
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex min-h-[48px] w-full items-center gap-3 rounded-[14px] bg-white/5 px-4 text-left text-[15px] text-kaiylo-txt transition-colors active:bg-white/10"
    >
      <Icon className="h-[18px] w-[18px] shrink-0 text-kaiylo-accent" aria-hidden="true" />
      <span className="flex-1 truncate">{label}</span>
      {dot && <span className="h-2 w-2 shrink-0 rounded-full bg-kaiylo-accent" aria-hidden="true" />}
      <ChevronRight className="h-4 w-4 shrink-0 text-kaiylo-txt-muted" aria-hidden="true" />
    </button>
  );
};

/**
 * Coach profile sheet on phones — the coach counterpart of
 * <StudentProfileSheet>, opened by the avatar of <CoachMobileHeader>. It
 * replaces the hamburger drawer there and also gathers what the desktop app
 * bar and sidebar hold that the five-tab bottom nav does not: Modèles,
 * Questionnaires, Facturation, Nouveautés, problem report, language, account
 * deletion.
 *
 * Changelog and account deletion stay owned by Header (the handlers come in
 * as props), so their modals are the same ones as on desktop.
 */
const CoachProfileSheet = ({
  isOpen,
  onClose,
  onOpenChangelog,
  hasUnseenChangelog = false,
  hasNewCandidatures = false,
  onDeleteAccount,
}) => {
  const { t } = useTranslation('common');
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const displayName = user?.name || user?.email || '';
  const initial = displayName.trim().charAt(0).toUpperCase() || 'C';

  // Close first, then act: the next overlay must not open under this sheet.
  const thenClose = (fn) => () => {
    onClose();
    fn?.();
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
    onClose();
  };

  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={onClose}
      labelledBy="coach-profile-sheet-name"
      testId="coach-profile-sheet"
      backdropTestId="coach-profile-sheet-backdrop"
    >
      <div className="flex items-center gap-3 px-5 pt-2 pb-5">
        <div
          aria-hidden="true"
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-kaiylo-accent text-[17px] font-bold text-white"
        >
          {initial}
        </div>
        <div className="min-w-0 flex-1">
          <p id="coach-profile-sheet-name" className="truncate text-[17px] font-bold text-kaiylo-txt">
            {displayName}
          </p>
          <p className="truncate text-[12px] text-kaiylo-txt-secondary">{t('coach_mobile.role')}</p>
        </div>
      </div>

      <nav className="flex flex-col gap-2 px-5 pb-5">
        <SheetRow icon={TemplatePuzzleGlyph} label={t('coach_mobile.templates')} onClick={thenClose(() => navigate('/coach/templates'))} />
        <SheetRow
          icon={ClipboardListIcon}
          label={t('nav.questionnaires')}
          dot={hasNewCandidatures}
          // Une candidature attend : directement sur l'onglet qui la montre.
          onClick={thenClose(() => navigate(hasNewCandidatures ? '/coach/questionnaires?tab=candidature' : '/coach/questionnaires'))}
        />
        <SheetRow icon={ReceiptIcon} label={t('nav.billing')} onClick={thenClose(() => navigate('/coach/facturation'))} />
        {onOpenChangelog && (
          <SheetRow icon={GiftIcon} label={t('coach_mobile.whats_new')} dot={hasUnseenChangelog} onClick={thenClose(onOpenChangelog)} />
        )}
      </nav>

      <div className="flex flex-col gap-2 px-5 pb-5">
        <span className="text-[11px] uppercase tracking-wider text-kaiylo-txt-secondary">
          {t('settings.language_label')}
        </span>
        <LanguageToggle />
      </div>

      <div className="flex flex-col gap-1 px-5">
        <button
          type="button"
          onClick={handleLogout}
          data-testid="coach-profile-sheet-logout"
          className="flex min-h-[44px] w-full items-center justify-center gap-2 rounded-[14px] text-[15px] font-semibold text-red-400 transition-colors"
          style={{ background: 'rgba(239, 68, 68, 0.12)' }}
        >
          <LogOut className="h-5 w-5" aria-hidden="true" />
          <span>{t('nav.logout')}</span>
        </button>
        {onDeleteAccount && (
          <button
            type="button"
            onClick={thenClose(onDeleteAccount)}
            className="min-h-[44px] w-full text-center text-[13px] text-kaiylo-txt-muted"
          >
            {t('settings.delete_account')}
          </button>
        )}
      </div>
    </BottomSheet>
  );
};

export default CoachProfileSheet;
